import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * <PageMeta> — per-page meta tags for SEO + Open Graph + Twitter cards.
 *
 * Drop this once per page (preferably as the first child of the page root)
 * and the browser/crawlers will see the right title, description, and OG
 * preview image for that route — instead of inheriting the static homepage
 * meta from index.html.
 *
 * Image is pulled from /api/og/<slug>.png (the dynamic Pillow-based generator)
 * unless an explicit imageUrl is passed.
 *
 * Notes for SPA crawlers:
 *   - Real-time crawlers (Slackbot, Twitterbot, Telegram, iMessage, WhatsApp,
 *     Discord, Facebook) execute JavaScript and pick up these dynamic tags.
 *   - Older / strict crawlers that don't execute JS still see the homepage
 *     fallback in index.html — which is also fine, just generic. Acceptable
 *     trade-off without SSR.
 */

// Make absolute URLs out of slug-style paths. og:image MUST be absolute.
const SITE_ORIGIN = (typeof window !== 'undefined' && window.location?.origin) || 'https://hibff.com';
// Backend lives on the same origin in production (api.hibff.com is reverse-proxied through hibff.com/api/*)
const API_BASE = process.env.REACT_APP_BACKEND_URL || SITE_ORIGIN;

const PageMeta = ({
  title,
  description,
  slug,
  path,
  canonicalPath,
  imageUrl,
  type = 'website',
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} · HiBFF` : 'HiBFF — Resilience for the AI generation';
  const desc = description ||
    'AvA is the AI companion you can practise difficult conversations with — before they happen in real life.';
  const url = `${SITE_ORIGIN}${path || '/'}`;
  const ogImage = imageUrl || (slug ? `${API_BASE}/api/og/${slug}.png` : `${API_BASE}/api/og/home.png`);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="HiBFF" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Canonical */}
      <link rel="canonical" href={`${SITE_ORIGIN}${canonicalPath || path || '/'}`} />
    </Helmet>
  );
};

export default PageMeta;
